import { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../../../utils/axiosInstance";
import { AuthContext } from "../../../context/AuthContext";
import { MODULES, isReadOnly } from "../../../utils/constants/permissions";
import { exportTable } from "../../../utils/exportTable";
import { btn } from "../../../components/Button/buttonStyles";

const fmtNum = (v) =>
  v !== undefined && v !== null && v !== "" ? Number(v).toFixed(2) : "0.00";

const fmtDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString("en-IN", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return "—"; }
};

const COVERAGE_CLS = {
  "Fully covered":     "text-green-600 font-medium",
  "Partially covered": "text-amber-600 font-medium",
  "Not covered":       "text-danger-500 font-medium",
};

const HEADERS = [
  "Code", "Linked invoices", "Invoice coverage",
  "Vendor", "Total amount", "Linked amount", "Unlinked amount",
  "Currency", "Payment method", "Paid at",
];

const linkedInvoices = (p) => (p.invoiceLinks || []).map((l) => l.invoice).filter(Boolean);

const methodName = (pm) =>
  pm?.bank_transfer_detail?.payment_method_name ||
  pm?.email_payment_detail?.email ||
  pm?.other_payment_detail?.payment_method_name ||
  pm?.payment_method?.replace(/_/g, " ") ||
  "";

const AdminVendorPaymentsPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  // Project managers may only view vendor payments (writes are 403 server-side).
  const readOnly = isReadOnly(user, MODULES.VENDOR_PAYMENTS);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState(
    sessionStorage.getItem("paymentSuccessMsg") || ""
  );

  useEffect(() => {
    sessionStorage.removeItem("paymentSuccessMsg");
    api.get("/project-finances/vendor-payments", { withCredentials: true })
      .then((res) => setPayments(res.data.data || []))
      .catch(() => setError("Failed to fetch vendor payments"))
      .finally(() => setLoading(false));
  }, []);

  const getVendorName = (vendor) => {
    if (!vendor) return "—";
    if (vendor.company_name) return vendor.company_name;
    const u = vendor.primary_users;
    return u ? `${u.first_name} ${u.last_name}`.trim() : "—";
  };

  // Mirrors HEADERS above, in the same order.
  const EXPORT_COLUMNS = [
    { header: "Code", value: (p) => p.payment_code || "" },
    { header: "Linked invoices", value: (p) => linkedInvoices(p).map((i) => i.invoice_code).join(", ") },
    { header: "Invoice coverage", value: (p) => p.coverage_by_invoices || "" },
    { header: "Vendor", value: (p) => getVendorName(p.vendor) },
    { header: "Total amount", value: (p) => fmtNum(p.amount) },
    { header: "Linked amount", value: (p) => fmtNum(p.linked_amount) },
    { header: "Unlinked amount", value: (p) => fmtNum(p.unlinked_amount) },
    { header: "Currency", value: (p) => p.currency || "" },
    { header: "Payment method", value: (p) => methodName(p.paymentMethod) },
    { header: "Paid at", value: (p) => fmtDate(p.paid_at) },
  ];

  return (
    <div className="space-y-4">
      {successMsg && (
        <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 flex justify-between items-center text-sm text-green-800">
          <span>{successMsg}</span>
          <button onClick={() => setSuccessMsg("")} className="text-green-600 hover:text-green-800 font-bold">✕</button>
        </div>
      )}

      <div className="flex justify-between items-center flex-wrap gap-3">
        <h1 className="text-[22px] font-semibold tracking-tight text-gray-900">Vendor Payments</h1>
        <div className="flex items-center gap-2 text-sm">
          {!readOnly && (
            <>
              <button onClick={() => navigate("/vendor/payments/new")} className={btn("primary", "sm")}>
                New payment
              </button>
              <button className={btn("secondary", "sm")}>
                Import from Excel
              </button>
            </>
          )}
          <button
            onClick={() => exportTable("vendor-payments", EXPORT_COLUMNS, payments)}
            className={btn("secondary", "sm")}
          >
            Export to Excel
          </button>
        </div>
      </div>

      {loading && (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border border-gray-300 border-t-brand-600" />
        </div>
      )}
      {error && <p className="text-danger-600 text-sm">{error}</p>}

      {!loading && !error && (
        <>
          <p className="text-sm text-gray-600">
            Showing <span className="font-semibold">{payments.length}</span> items.
          </p>

          <div className="w-full bg-white shadow rounded-lg overflow-hidden">
            <div className="max-h-[600px] overflow-y-auto overflow-x-auto">
              <table className="w-full table-auto border-collapse min-w-[1100px]">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-white border-b text-[10px] tracking-widest text-gray-500 uppercase">
                    {HEADERS.map((h) => (
                      <th key={h} className="px-3 py-2 font-semibold text-center whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {payments.length === 0 ? (
                    <tr>
                      <td colSpan={HEADERS.length} className="py-10 text-center text-gray-400 text-xs">
                        No vendor payments found.
                      </td>
                    </tr>
                  ) : (
                    payments.map((p, i) => {
                      const coverageCls = COVERAGE_CLS[p.coverage_by_invoices] ?? "text-gray-600";
                      const pmType = methodName(p.paymentMethod) || "—";
                      const vendorName = getVendorName(p.vendor);
                      const rowBg = i % 2 === 0 ? "bg-gray-50" : "bg-white";

                      return (
                        <tr
                          key={p.id}
                          className={`${rowBg} hover:bg-brand-50 transition-colors`}
                        >
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center">
                            <Link
                              to={`/vendor/payment/${p.id}`}
                              className="text-brand-600 hover:text-brand-800 font-medium underline"
                            >
                              {p.payment_code}
                            </Link>
                          </td>
                          <td className="px-3 py-2 text-xs text-center">
                            {linkedInvoices(p).length ? (
                              <div className="flex flex-wrap justify-center gap-x-2">
                                {linkedInvoices(p).map((inv) => (
                                  <Link key={inv.id} to={`/vendor/invoice/${inv.id}`} className="text-brand-600 hover:underline font-medium">
                                    {inv.invoice_code}
                                  </Link>
                                ))}
                              </div>
                            ) : "—"}
                          </td>
                          <td className={`px-3 py-2 text-xs text-center ${coverageCls}`}>
                            {p.coverage_by_invoices || "—"}
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center">
                            {p.vendor_id ? (
                              <Link to={`/vendors/${p.vendor_id}`} className="text-brand-600 hover:underline font-medium">
                                {vendorName}
                              </Link>
                            ) : vendorName}
                          </td>
                          <td className="px-3 py-2 text-xs font-semibold text-right whitespace-nowrap">{fmtNum(p.amount)}</td>
                          <td className="px-3 py-2 text-xs text-right whitespace-nowrap text-gray-900">{fmtNum(p.linked_amount)}</td>
                          <td className="px-3 py-2 text-xs text-right whitespace-nowrap text-gray-900">{fmtNum(p.unlinked_amount)}</td>
                          <td className="px-3 py-2 text-xs text-center">{p.currency || "—"}</td>
                          <td className="px-3 py-2 text-xs capitalize text-center">{pmType}</td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-gray-500">{fmtDate(p.paid_at)}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminVendorPaymentsPage;
